import React, { useEffect, useState } from 'react';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { Form, Formik, FormikProps } from 'formik';
import { useDebounce } from 'usehooks-ts';
import { useGetAllTagTypes, useGetAllTags } from '@juno/client-api';
import {
  ComponentData,
  Component as ComponentModel,
  MentorshipStatusEnum,
  Tag,
  TagType,
} from '@juno/client-api/model';
import { FeatureConfig } from '@juno/client-api/utils';
import { FEATURE_CONFIG_KEYS_ENUM } from '@juno/constants';
import { AdditionalFilter, GenericFormikInput, JunoSpin } from '@juno/ui';
import { FEATURE_FLAG, useFeatureFlagWithUser, useSettings } from '@juno/utils';
import { DIRECTORY_DEFAULT_NUM_PER_PAGE } from './DirectoryPagination';

interface DirectoryFormProps {
  component?: ComponentModel;
  onSave: (component: ComponentModel) => void;
  onUpdate: (component: ComponentModel) => void;
  formik: FormikProps<DirectoryComponentModel>;
}

interface DirectoryFormWrapperProps {
  component?: ComponentModel;
  onSave: (component: ComponentModel) => void;
  onUpdate: (component: ComponentModel) => void;
}

interface MentorshipStatusValue {
  id: MentorshipStatusEnum;
  value: string;
}

export interface AdditionalFilterOption {
  id: number;
  value: string;
  keyword: string;
}

export interface ResourceFilter {
  id: string;
}

export interface DirectoryDataModel extends ComponentData {
  title: string;
  numPerPage: number;
  showSavedUsers: boolean;
  showRecommendedUsers: boolean;
  interestsFilter: ResourceFilter[];
  skillsFilter: ResourceFilter[];
  mentorshipFilter?: MentorshipStatusValue[];
  filterTagTypes: ResourceFilter[];
  filterMentorshipStatuses: MentorshipStatusValue[];
  additionalFilterOptions: AdditionalFilterOption[];
  additionalFilters: AdditionalFilter[];
}

export interface DirectoryComponentModel extends ComponentModel {
  data: DirectoryDataModel;
}

const DirectoryForm: React.FC<DirectoryFormProps> = ({ component, onUpdate, formik }) => {
  const { site, configs } = useSettings();

  // Configcat - feature flags
  const { value: directoryFiltersEnabled } = useFeatureFlagWithUser(
    FEATURE_FLAG.DIRECTORY_TAG_FILTERS,
  );

  // Feature Config - feature flags
  const mentorshipEnabled = FeatureConfig.getFeatureFlag(
    configs,
    FEATURE_CONFIG_KEYS_ENUM.Mentorship,
  );

  const { setValues, values } = formik;
  const debouncedValues = useDebounce(values, 700);
  const [additionalFilterOptions, setAdditionalFilterOptions] = useState<AdditionalFilterOption[]>(
    [],
  );
  const [additionalFilters, setAdditionalFilters] = useState<AdditionalFilter[]>([]);

  const { data: tags, isLoading: isLoadingTags } = useGetAllTags(
    site?.id || '',
    {
      order: 'type__value,value',
    },
    { query: { enabled: !!site?.id } },
  );

  const { data: tagTypes, isLoading: isLoadingTagTypes } = useGetAllTagTypes(
    site?.id || '',
    {},
    { query: { enabled: !!site?.id } },
  );

  const mentorshipStatuses = Object.values(MentorshipStatusEnum).map((status) => {
    const unhandled = (_: never): never => _;
    switch (status) {
      case MentorshipStatusEnum.NUMBER_0:
        return {
          id: MentorshipStatusEnum.NUMBER_0,
          value: 'Mentor',
        };
      case MentorshipStatusEnum.NUMBER_1:
        return {
          id: MentorshipStatusEnum.NUMBER_1,
          value: 'Mentee',
        };
      case MentorshipStatusEnum.NUMBER_2:
        return {
          id: MentorshipStatusEnum.NUMBER_2,
          value: 'Not Applicable',
        };
      default:
        return unhandled(status);
    }
  });

  useEffect(() => {
    // Setting additional filter options
    // Additional filter options should adhere to the AdditionalFilterOption interface

    if (mentorshipEnabled) {
      // Only add Mentorship options when the mentorship feature flag is enabled
      setAdditionalFilterOptions([
        ...additionalFilterOptions,
        {
          id: 1,
          value: 'Mentorship Status',
          keyword: 'mentorship_status',
        },
      ]);

      // Add additional filters for mentorship
      setAdditionalFilters([
        ...additionalFilters,
        {
          id: MentorshipStatusEnum.NUMBER_0,
          type: 1,
          value: 'Mentor',
        },
        {
          id: MentorshipStatusEnum.NUMBER_1,
          type: 1,
          value: 'Mentee',
        },
        {
          id: MentorshipStatusEnum.NUMBER_2,
          type: 1,
          value: 'Not Applicable',
        },
      ]);
    }

    // Setting additional filters
    // Additional filters shoud adhere to the AdditionalFilter Interface

    // ADD ADDITIONAL FILTER OPTIONS AND FILTERS HERE WHEN NEEDED
  }, []);

  const getAdditionalFilters = (additionalFilterOptions: AdditionalFilterOption[]) => {
    return additionalFilters.filter((filter) =>
      additionalFilterOptions.some((option) => option.id === filter.type),
    );
  };

  useEffect(() => {
    if (component) {
      onUpdate({ ...component, ...debouncedValues });
    }
  }, [debouncedValues]);

  if (isLoadingTagTypes || isLoadingTags) return <JunoSpin />;

  return (
    <>
      <Typography variant='body2' sx={{ opacity: 0.6 }}>
        Editing Section
      </Typography>
      <Typography variant='h5' sx={{ mb: 4 }}>
        {values.name || 'Directory'}
      </Typography>
      <GenericFormikInput
        name='name'
        label='Name'
        type='text'
        value={values.name}
        InputProps={{ disableUnderline: true }}
        variant='filled'
        fullWidth
      />
      <GenericFormikInput
        name='data.title'
        label='Title'
        type='text'
        value={values.data.title}
        InputProps={{ disableUnderline: true }}
        variant='filled'
        fullWidth
      />
      <Box mb={2}>
        <Tooltip
          title='This will toggle visibility of the Saved Users section at the top of the Directory.'
          placement='top-start'
        >
          <FormControlLabel
            value={values?.data?.showSavedUsers}
            control={
              <Checkbox
                checked={values?.data?.showSavedUsers}
                defaultChecked={true}
                onChange={(e) =>
                  setValues({
                    ...values,
                    data: {
                      ...values.data,
                      showSavedUsers: e.target.checked,
                    },
                  })
                }
              />
            }
            label='Show saved users'
          />
        </Tooltip>
        <Tooltip
          title='This will toggle visibility of the Recommended Users section of the Directory.'
          placement='top-start'
        >
          <FormControlLabel
            value={values?.data?.showRecommendedUsers}
            control={
              <Checkbox
                checked={values?.data?.showRecommendedUsers}
                defaultChecked={true}
                onChange={(e) =>
                  setValues({
                    ...values,
                    data: {
                      ...values.data,
                      showRecommendedUsers: e.target.checked,
                    },
                  })
                }
              />
            }
            label='Show recommended users'
          />
        </Tooltip>
      </Box>
      <Stack spacing={1}>
        {mentorshipEnabled && (
          <Autocomplete
            id='autocomplete-mentorship-status'
            disableCloseOnSelect
            multiple
            onChange={(
              event: React.SyntheticEvent<Element, Event>,
              value: MentorshipStatusValue[],
            ) => {
              formik.setFieldValue('data.mentorshipFilter', value);
            }}
            getOptionLabel={(option) => `${option.value}`}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize='small' />}
                  checkedIcon={<CheckBoxIcon fontSize='small' />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {`${option.value}`}
              </li>
            )}
            options={mentorshipStatuses || []}
            value={values?.data?.mentorshipFilter || []}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={'Filter by mentorship status'}
                variant='filled'
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                  endAdornment: (
                    <React.Fragment>
                      {isLoadingTags ? <CircularProgress color='inherit' size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        )}
        <Autocomplete
          id='autocomplete-user-interests'
          disableCloseOnSelect
          multiple
          onChange={(event: React.SyntheticEvent<Element, Event>, value: Tag[]) => {
            formik.setFieldValue(
              'data.interestsFilter',
              value.map((tag) => ({ id: tag.id })),
            );
          }}
          getOptionLabel={(option) => `${option?.value}`}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize='small' />}
                checkedIcon={<CheckBoxIcon fontSize='small' />}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {`${option.value}`}
            </li>
          )}
          options={tags?.filter((t) => t.type.value == 'Interests') || []}
          groupBy={(option) => option.type.value}
          loading={isLoadingTags}
          value={tags?.filter((tag) =>
            values.data.interestsFilter?.some((filterTag) => filterTag.id === tag.id),
          )}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label={'Filter by Interest'}
              variant='filled'
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                endAdornment: (
                  <React.Fragment>
                    {isLoadingTags ? <CircularProgress color='inherit' size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />
        <Autocomplete
          id='autocomplete-user-skills'
          disableCloseOnSelect
          multiple
          onChange={(event: React.SyntheticEvent<Element, Event>, value: Tag[]) => {
            formik.setFieldValue(
              'data.skillsFilter',
              value.map((tag) => ({ id: tag.id })),
            );
          }}
          getOptionLabel={(option) => `${option?.value}`}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize='small' />}
                checkedIcon={<CheckBoxIcon fontSize='small' />}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {`${option.value}`}
            </li>
          )}
          options={tags?.filter((t) => t.type.value == 'Skills') || []}
          groupBy={(option) => option.type.value}
          loading={isLoadingTags}
          value={tags?.filter((tag) =>
            values.data.skillsFilter?.some((filterTag) => filterTag.id === tag.id),
          )}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label={'Filter by Skill'}
              variant='filled'
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                endAdornment: (
                  <React.Fragment>
                    {isLoadingTags ? <CircularProgress color='inherit' size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />
        <Tooltip title='Number of users to display in the full list.' placement='top-start'>
          <Select
            value={values?.data?.numPerPage || 10}
            name='data.numPerPage'
            onChange={(e) =>
              setValues({
                ...values,
                data: {
                  ...values.data,
                  numPerPage: e.target.value as number,
                },
              })
            }
            fullWidth
            defaultValue={DIRECTORY_DEFAULT_NUM_PER_PAGE}
          >
            <MenuItem value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={25}>25</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </Tooltip>
        {directoryFiltersEnabled && (
          <Autocomplete
            id='autocomplete-filter-tag-types'
            disableCloseOnSelect
            multiple
            onChange={(event: React.SyntheticEvent<Element, Event>, value: TagType[]) => {
              formik.setFieldValue(
                'data.filterTagTypes',
                value.map((tagType) => ({
                  id: tagType.id,
                })),
              );
            }}
            getOptionLabel={(option) => `${option?.value}`}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize='small' />}
                  checkedIcon={<CheckBoxIcon fontSize='small' />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {`${option.value}`}
              </li>
            )}
            options={tagTypes || []}
            loading={isLoadingTagTypes}
            value={tagTypes?.filter((tagType) =>
              values.data.filterTagTypes?.some((filterTagType) => filterTagType.id === tagType.id),
            )}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={'Tag Types to Display as Filters'}
                variant='filled'
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                  endAdornment: (
                    <React.Fragment>
                      {isLoadingTagTypes ? <CircularProgress color='inherit' size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        )}
        {additionalFilterOptions.length > 0 && (
          <Autocomplete
            id='autocomplete-aditional-filter-options'
            disableCloseOnSelect
            multiple
            onChange={(
              event: React.SyntheticEvent<Element, Event>,
              value: AdditionalFilterOption[],
            ) => {
              formik.setFieldValue('data.additionalFilterOptions', value);
              formik.setFieldValue('data.additionalFilters', getAdditionalFilters(value));
            }}
            getOptionLabel={(option) => `${option.value}`}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize='small' />}
                  checkedIcon={<CheckBoxIcon fontSize='small' />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {`${option.value}`}
              </li>
            )}
            options={additionalFilterOptions || []}
            value={values?.data?.additionalFilterOptions || []}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={'Additional filter options'}
                variant='filled'
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                  endAdornment: <React.Fragment>{params.InputProps.endAdornment}</React.Fragment>,
                }}
              />
            )}
          />
        )}
      </Stack>
    </>
  );
};

const DirectoryFormWrapper: React.FC<DirectoryFormWrapperProps> = ({
  component,
  onSave,
  onUpdate,
}) => {
  return (
    <Formik
      initialValues={(component as DirectoryComponentModel) || ({} as DirectoryComponentModel)}
      onSubmit={async (values, { setSubmitting, resetForm }) => {
        if (component) {
          onSave({ ...component, ...values });
        }
      }}
      enableReinitialize
    >
      {(formik) => {
        return (
          <Form>
            <DirectoryForm
              component={component}
              onSave={onSave}
              onUpdate={onUpdate}
              formik={formik}
            />
          </Form>
        );
      }}
    </Formik>
  );
};

export default DirectoryFormWrapper;
